'use memo'

import React from 'react'
import styled from 'styled-components'
import { Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  width: 100%;
`

const LoadingText = styled.p`
  margin-top: 16px;
  color: ${colors.NEUTRAL_100};
  font-size: 14px;
`

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: ${colors.SECONDARY_300};
  border-radius: 8px;
  margin-bottom: 8px;
`

const SkeletonCell = styled.div<{ width?: string }>`
  height: 20px;
  background: ${colors.SECONDARY_200};
  border-radius: 4px;
  width: ${(props) => props.width || '100px'};
  margin-right: 16px;
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 24px;
  text-align: center;
`

const ErrorText = styled.p`
  color: ${colors.ERROR_300};
  font-size: 16px;
  margin-bottom: 16px;
`

const RetryButton = styled.button`
  padding: 8px 24px;
  background: ${colors.PRIMARY};
  color: ${PRIMARY_BUTTON_CONTENT.text};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`

interface PageLoadingProps {
  message?: string
}

export const PageLoading: React.FC<PageLoadingProps> = ({ message = '' }) => {
  return (
    <LoadingContainer>
      <Spin size='small' />
      <LoadingText>{message}</LoadingText>
    </LoadingContainer>
  )
}

interface TableSkeletonProps {
  rows?: number
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 5 }) => {
  return (
    <div>
      {Array.from({ length: rows }).map((_, index) => (
        <SkeletonRow key={index}>
          <SkeletonCell width='40px' />
          <SkeletonCell width='200px' />
          <SkeletonCell width='120px' />
          <SkeletonCell width='80px' />
          <SkeletonCell width='100px' />
        </SkeletonRow>
      ))}
    </div>
  )
}

interface ErrorStateProps {
  error: Error | null
  onRetry?: () => void
}

export const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry }) => {
  return (
    <ErrorContainer>
      <ErrorText>
        {error?.message || 'An error occurred while loading the data'}
      </ErrorText>
      {onRetry && <RetryButton onClick={onRetry}>Try Again</RetryButton>}
    </ErrorContainer>
  )
}

interface EmptyStateProps {
  message?: string
  description?: string
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  message = 'No data found',
  description,
}) => {
  return (
    <ErrorContainer>
      <ErrorText style={{ color: colors.NEUTRAL_100 }}>{message}</ErrorText>
      {description && (
        <p style={{ color: colors.NEUTRAL_200, fontSize: 14 }}>{description}</p>
      )}
    </ErrorContainer>
  )
}

interface InlineLoadingProps {
  size?: 'small' | 'default' | 'large'
}

export const InlineLoading: React.FC<InlineLoadingProps> = ({
  size = 'small',
}) => {
  return <Spin size={size} />
}
